import React from 'react';
import { Warning as WarningIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { InvitationErrorDetails } from 'src/types/errors';

interface InvitationErrorPopupProps {
  isOpen: boolean;
  onClose: () => void;
  errors: InvitationErrorDetails[];
  successfulInvitations?: string[];
}

export const InvitationErrorPopup: React.FC<InvitationErrorPopupProps> = ({
  isOpen,
  onClose,
  errors,
  successfulInvitations = [],
}) => {
  const theme = useTheme();

  const getTitle = () => {
    if (successfulInvitations.length > 0 && errors.length > 0) {
      return 'Invitation Results';
    }
    return 'Invitation Errors';
  };

  const getDescription = () => {
    if (successfulInvitations.length > 0 && errors.length > 0) {
      return `${successfulInvitations.length} invitation(s) sent successfully, but ${errors.length} invitation(s) failed:`;
    }
    return `${errors.length} invitation(s) could not be sent:`;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      data-testid='invitation-error-popup'
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <WarningIcon
          color='warning'
          sx={{ fontSize: theme.typography.h5.fontSize }}
        />
        <Typography variant='h6' component='span'>
          {getTitle()}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography
          variant='body2'
          sx={{
            color: 'text.secondary',
            mb: 2,
          }}
        >
          {getDescription()}
        </Typography>

        {successfulInvitations.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant='subtitle2'
              sx={{
                color: 'success.main',
                mb: 1,
              }}
            >
              Successfully invited ({successfulInvitations.length}):
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'text.secondary',
              }}
            >
              {successfulInvitations.join(', ')}
            </Typography>
          </Box>
        )}

        <List disablePadding>
          {errors.map((error, index) => (
            <ListItem
              key={index}
              disableGutters
              sx={{
                py: 1,
                px: 0,
                alignItems: 'flex-start',
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                <WarningIcon
                  color='warning'
                  sx={{ fontSize: theme.typography.body2.fontSize }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'text.primary',
                    }}
                  >
                    {error.message}
                  </Typography>
                }
                sx={{ my: 0 }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          variant='contained'
          size='large'
          sx={{ minWidth: 100 }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};
